import { cn } from '@/lib/cn'

export type BadgeVariant = 'success' | 'danger' | 'warning' | 'neutral' | 'info'
export type BadgeSize = 'sm' | 'md'

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  size?: BadgeSize
  /** Render a leading status dot tinted to the variant. */
  dot?: boolean
  children: React.ReactNode
}

/**
 * Variant colors are built from one recipe — tinted surface, tinted border, and
 * a text tone dark enough to clear 4.5:1 on that surface in both themes.
 */
const variantClasses: Record<BadgeVariant, string> = {
  success:
    'border-green-600/20 bg-green-50 text-green-800 dark:border-green-400/25 dark:bg-green-400/10 dark:text-green-300',
  danger:
    'border-red-600/20 bg-red-50 text-red-800 dark:border-red-400/25 dark:bg-red-400/10 dark:text-red-300',
  warning:
    'border-amber-600/25 bg-amber-50 text-amber-900 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-300',
  neutral:
    'border-gray-600/15 bg-gray-100 text-gray-700 dark:border-gray-400/20 dark:bg-gray-400/10 dark:text-gray-300',
  info:
    'border-blue-600/20 bg-blue-50 text-blue-800 dark:border-blue-400/25 dark:bg-blue-400/10 dark:text-blue-300',
}

const dotClasses: Record<BadgeVariant, string> = {
  success: 'bg-green-600 dark:bg-green-400',
  danger: 'bg-red-600 dark:bg-red-400',
  warning: 'bg-amber-500 dark:bg-amber-400',
  neutral: 'bg-gray-500 dark:bg-gray-400',
  info: 'bg-blue-600 dark:bg-blue-400',
}

const sizeClasses: Record<BadgeSize, string> = {
  /** Dense contexts: table cells, grid chips, counts. */
  sm: 'min-h-5 gap-1 px-1.5 py-0.5 text-[10px]',
  md: 'min-h-6 gap-1.5 px-2 py-0.5 text-[11px]',
}

/**
 * Compact status label. Sizes are capped so a badge never grows taller than the
 * line it annotates — dense call sites should pass `size="sm"` rather than
 * overriding padding.
 */
export const Badge = ({
  variant = 'neutral',
  size = 'md',
  dot = false,
  className,
  children,
  ...rest
}: BadgeProps): React.ReactElement => {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center truncate whitespace-nowrap rounded-full border font-semibold leading-none tracking-[0.01em]',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      {...rest}
    >
      {dot && (
        <span
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            dotClasses[variant]
          )}
          aria-hidden
        />
      )}
      {children}
    </span>
  )
}

Badge.displayName = 'Badge'
